import { canOpenPortalJob } from '../../../utils/portalJobNavigation';
import React, { useCallback, useRef, useState } from "react";
import Table from "react-bootstrap/Table";

const getDisplayValue = (field, job) => {
  const value = field.render ? field.render(job) : job[field.key];
  return value === null || value === undefined || value === "" ? "—" : value;
};

const CompactScheduleSection = ({ title, jobs, dateLabel, getDateValue, fields }) => {
  const [expandedJobs, setExpandedJobs] = useState(() => new Set());
  const headerViewportRef = useRef(null);
  const rowViewportRefs = useRef(new Map());
  const isSynchronizingScroll = useRef(false);

  const toggleJob = useCallback((jobKey) => {
    setExpandedJobs((currentJobs) => {
      const nextJobs = new Set(currentJobs);
      if (nextJobs.has(jobKey)) {
        nextJobs.delete(jobKey);
      } else {
        nextJobs.add(jobKey);
      }
      return nextJobs;
    });
  }, []);

  const synchronizeScroll = useCallback((event) => {
    if (isSynchronizingScroll.current) return;

    const source = event.currentTarget;
    isSynchronizingScroll.current = true;

    if (headerViewportRef.current && headerViewportRef.current !== source) {
      headerViewportRef.current.scrollLeft = source.scrollLeft;
    }

    rowViewportRefs.current.forEach((viewport) => {
      if (viewport !== source) viewport.scrollLeft = source.scrollLeft;
    });

    window.requestAnimationFrame(() => {
      isSynchronizingScroll.current = false;
    });
  }, []);

  const registerRowViewport = useCallback((jobKey, node) => {
    if (node) {
      rowViewportRefs.current.set(jobKey, node);
    } else {
      rowViewportRefs.current.delete(jobKey);
    }
  }, []);

  return (
    <section className="schedule-compact-week">
      <h5 className="schedule-compact-week-heading">{title}</h5>

      {expandedJobs.size > 0 && (
        <div
          className="schedule-compact-shared-header-viewport"
          ref={headerViewportRef}
          onScroll={synchronizeScroll}
          role="region"
          aria-label={`${title} detail columns`}
          tabIndex="0"
        >
          <Table bordered className="schedule-compact-metrics-table schedule-compact-header-table schedule-simple-metrics-table">
            <thead>
              <tr>
                {fields.map((field) => (
                  <th key={field.key} className={`header-cell ${field.className || ""}`.trim()}>
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
          </Table>
        </div>
      )}

      {jobs.map((job, index) => {
        const jobKey = `${title}-${job.ID || job.JobNumber || index}`;
        const detailsId = `compact-details-${jobKey}`.replace(/[^a-zA-Z0-9_-]/g, "-");
        const isExpanded = expandedJobs.has(jobKey);

        return (
          <article className="schedule-compact-job" key={jobKey}>
            <div className="schedule-compact-summary">
              <div className="schedule-compact-identity-field schedule-compact-job-number">
                {job.ID && canOpenPortalJob(job) ? (
                  <a href={`/${job.ID}/generalInfo`}>{job.JobNumber || "Unassigned"}</a>
                ) : (
                  <span>{job.JobNumber || "Unassigned"}</span>
                )}
              </div>
              <div className="schedule-compact-identity-field schedule-compact-job-name">
                <span>{job.ProjectName || "No project name"}</span>
              </div>
              <div className="schedule-compact-ship-date">
                <span className="schedule-compact-ship-date-label">{dateLabel}</span>
                <span>{getDateValue(job) || "Not set"}</span>
              </div>
              <button
                type="button"
                className="schedule-compact-expand-button"
                onClick={() => toggleJob(jobKey)}
                aria-expanded={isExpanded}
                aria-controls={detailsId}
                aria-label={`${isExpanded ? "Hide" : "Show"} details for ${job.JobNumber || job.ProjectName || "job"}`}
              >
                <i className={`bi bi-chevron-${isExpanded ? "up" : "down"}`} aria-hidden="true" />
              </button>
            </div>

            <div className="schedule-compact-details" id={detailsId} hidden={!isExpanded}>
              <div
                className="schedule-compact-metrics-viewport"
                ref={(node) => registerRowViewport(jobKey, node)}
                onScroll={synchronizeScroll}
                role="region"
                aria-label={`Details for ${job.JobNumber || job.ProjectName || "job"}`}
                tabIndex="0"
              >
                <Table bordered className="schedule-compact-metrics-table schedule-compact-values-table schedule-simple-metrics-table">
                  <tbody>
                    <tr>
                      {fields.map((field) => (
                        <td key={field.key} className={field.className || ""}>
                          {getDisplayValue(field, job)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
};

export default CompactScheduleSection;
